import { lazy } from "react";
import { defineRoute } from "./routeTypes";
import Welcome from "./pages/Welcome";
import SubscriptionWarning from "./pages/SubscriptionWarning";

const DesignViewer = lazy(() => import("@Features/design-viewer/components/DesignViewer/DesignViewer"));
const Issues = lazy(() => import("@Features/issues/components/Issues/Issues"));
const IssuesList = lazy(() => import("@Features/issues/pages/Issues/IssuesList"));
const PhotosList = lazy(() => import("@Features/photos/pages/Photos/PhotosList"));
const FileExplorer = lazy(() => import("@Features/files/pages/FileExplorer"));
// Not lazy: it only redirects the legacy explorer URL to /project/:projectId/folders, so load it eagerly
// (no chunk fetch before the redirect fires).
import RedirectExplorerToFolders from "@Features/files/pages/FileExplorer/RedirectExplorerToFolders";
const MobileExplorer = lazy(() => import("@Features/files/pages/FileExplorer/Mobile.jsx"));
const TaskDetails = lazy(() => import("./pages/TaskManagement/TaskDetails/TaskDetails"));
const TransmitalsList = lazy(
  () => import("@Features/transmittals/pages/Transmitals/ListTransmittals/listTransmittals"),
);
const TransmitalDetails = lazy(
  () => import("@Features/transmittals/pages/Transmitals/TransmitalDetails/TransmitalDetails"),
);
const TaskList = lazy(() => import("./pages/TaskManagement/ListTask/listTask"));
const PortalDashboard = lazy(() => import("@Features/portal/dashboard"));
const ProjectDashboard = lazy(() =>
  import("@Features/projects").then((module) => ({ default: module.ProjectDashboard })),
);
const PortalSetting = lazy(() =>
  import("@Features/portal-settings").then((module) => ({ default: module.PortalSettingsPage })),
);
const UserProfile = lazy(() => import("./pages/UserProfile"));
const ProjectSettings = lazy(() =>
  import("@Features/projects").then((module) => ({ default: module.ProjectSettings })),
);
const NotificationsPage = lazy(() =>
  import("@Features/portal").then((module) => ({ default: module.NotificationsPage })),
);
const EscrowFiles = lazy(() => import("@Features/portal").then((module) => ({ default: module.EscrowFilesPage })));
const Workspaces = lazy(() => import("./pages/Workspaces"));
const ProjectsPage = lazy(() => import("@Features/projects").then((module) => ({ default: module.ProjectsPage })));
const LoginCallback = lazy(() => import("./components/LoginCallback/LoginCallback"));
const LinkingAccount = lazy(() => import("./components/LinkingAccount"));
const SignInPage = lazy(() => import("./pages/SignIn"));
const OrganisationSettings = lazy(() => import("./pages/OrganisationSettings/OrganisationSettings"));
const EstimateListPage = lazy(() =>
  import("@Features/estimation").then((module) => ({ default: module.EstimateListPage })),
);
const Estimate = lazy(() => import("@Features/estimation").then((module) => ({ default: module.EstimatePage })));
const Forbidden = lazy(() => import("./pages/Error/Forbidden"));
const ScheduleListPage = lazy(() => import("./pages/Schedules/ScheduleListPage"));
const ContractsPage = lazy(() => import("@Features/contracts/pages/Contracts"));
const ContractDetails = lazy(() => import("@Features/contracts/pages/Contracts/ContractDetails"));
const FormsList = lazy(() => import("@Features/forms/pages/Forms"));
const GoogleSigninButton = lazy(() => import("./components/GoogleSigninButton"));
const ThankYou = lazy(() => import("./components/ThankYou"));
const ScheduleDetailsNew = lazy(() => import("./pages/Schedules/ScheduleDetailsNew"));
const CreateProject = lazy(() => import("@Features/projects").then((module) => ({ default: module.CreateNewProject })));
const ClashTests = lazy(() => import("./pages/ClashTests"));
const ClashTestDetails = lazy(() => import("./pages/ClashTests/ClashDetails"));
const ProjectFiles = lazy(() => import("@Features/files/pages/Files/index2"));
const AssetsList = lazy(() => import("./pages/Assets/index"));
const AssetRelatedDocuments = lazy(() => import("./pages/Assets/RelatedDocuments"));
const PortalFiles = lazy(() => import("@Features/portal/pages/Files/PortalFiles"));
const CreateInspectionAndProgres = lazy(() =>
  import("@Features/forms").then((module) => ({ default: module.CreateInspectionAndProgressPage })),
);
const EstimatePortalList = lazy(() =>
  import("@Features/estimation").then((module) => ({ default: module.EstimatePortalListPage })),
);
const ReviewPage = lazy(() => import("@Features/reviews/ReviewPage"));
const Mailbox = lazy(() => import("@Features/mailbox/pages/Mailbox/index"));
const Reviews = lazy(() => import("@Features/reviews/ReviewsPage"));
const MailThread = lazy(() => import("@Features/mailbox/pages/Mailbox/MailThread"));
const PdfGenerator = lazy(() => import("@Features/pdf-viewer/components/PdfGenerator/PdfGenerator"));
const AdminBilling = lazy(() => import("@Features/portal-settings/pages/AdminBilling/AdminBilling"));
const SignupFlow = lazy(() => import("./components/SignupFlow/SignupFlow"));
const InviteUser = lazy(() => import("./components/SignupFlow/InviteUser"));
const ForgotPassword = lazy(() => import("./components/SignupFlow/ForgotPassword"));
const AiChat = lazy(() => import("./pages/AiChat"));
const SignInUser = lazy(() => import("./components/SignupFlow/SignInUser"));
// Recipient-facing shareable-link viewer (CP-283). Unauthenticated: rendered with no header and no
// sidebar, and excluded from the sign-in bounce in Router.tsx.
const SharedLinkPage = lazy(() => import("@Features/external-share/pages/SharedLinkPage"));
// The recipient's file viewer. Renders the real DesignViewer, so PDF/BIM/CAD/image/video/sheet all
// behave exactly as they do internally.
const SharedFileViewerPage = lazy(() => import("@Features/external-share/pages/SharedFileViewerPage"));

const routes = [
  defineRoute({
    path: ["/resetPassword"],
    element: SignInPage,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/googleSigninButton"],
    element: GoogleSigninButton,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/thank-you"],
    element: ThankYou,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/signup", "/95789"],
    element: SignInPage,
    props: { formType: "signup" },
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/invitation"],
    element: SignInPage,
    props: { formType: "invitation" },
    showStickySidebar: false,
    showHeader: false,
    searchParams: { id: "objectId" },
  }),
  defineRoute({
    path: ["/user-profile/:settingType"],
    element: UserProfile,
    params: { settingType: "slug" },
    showStickySidebar: false,
  }),
  defineRoute({
    path: ["/dashboard"],
    element: PortalDashboard,
    showStickySidebar: false,
  }),
  defineRoute({
    path: ["/welcome"],
    element: Welcome,
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/subscriptionWarning"],
    element: SubscriptionWarning,
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/googleLoginCallback"],
    element: LoginCallback,
    showStickySidebar: false,
  }),
  defineRoute({
    path: ["/linkingAccount"],
    element: LinkingAccount,
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/projects"],
    element: ProjectsPage,
    showStickySidebar: false,
  }),
  defineRoute({ path: ["/403"], element: Forbidden, showStickySidebar: false }),
  defineRoute({ path: ["/project/:projectId/mails"], element: Mailbox, params: { projectId: "objectId" } }),
  defineRoute({ path: ["/reviews"], element: Reviews }),
  defineRoute({
    path: ["/project/:projectId/mails/:mailId/mailThread"],
    element: MailThread,
    params: { projectId: "objectId", mailId: "objectId" },
  }),
  // Legacy explorer URL: redirect to the new project-level Folders URL (prefixing the Space id onto the hash).
  defineRoute({
    path: ["/workspace/:workspaceId/explorer"],
    element: RedirectExplorerToFolders,
    params: { workspaceId: "objectId" },
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/project/:projectId/folders"],
    element: FileExplorer,
    params: { projectId: "objectId" },
    searchParams: { spaceId: "objectId" },
  }),
  defineRoute({
    path: ["/workspace/:workspaceId/mobileExplorer"],
    element: MobileExplorer,
    params: { workspaceId: "objectId" },
    showStickySidebar: false,
    showHeader: false,
    searchParams: { projectId: "objectId" },
  }),
  defineRoute({ path: ["/Files"], element: PortalFiles }),
  defineRoute({
    path: ["/workspace/:workspaceId/file/:fileid/issues"],
    element: Issues,
    params: { workspaceId: "objectId", fileid: "objectId" },
  }),
  defineRoute({
    path: ["/project/:projectId/issues", "/issuesList"],
    element: IssuesList,
    params: { projectId: "objectId" },
  }),
  defineRoute({ path: ["/project/:projectId/photos"], element: PhotosList, params: { projectId: "objectId" } }),
  defineRoute({
    path: ["/project/:projectId/setting"],
    element: ProjectSettings,
    params: { projectId: "objectId" },
    searchParams: { spaceId: "objectId" },
  }),
  defineRoute({
    path: ["/project/:projectId/listTransmittals"],
    element: TransmitalsList,
    params: { projectId: "objectId" },
    exact: true,
  }),
  defineRoute({
    path: ["/project/:projectId/listFiles"],
    element: ProjectFiles,
    params: { projectId: "objectId" },
    exact: true,
  }),
  defineRoute({
    path: ["/project/:projectId/assetsList"],
    element: AssetsList,
    params: { projectId: "objectId" },
    exact: true,
  }),
  defineRoute({
    path: ["/project/:projectId/assetsList/:assetId/relatedDocuments"],
    element: AssetRelatedDocuments,
    params: { projectId: "objectId", assetId: "objectId" },
    exact: true,
  }),
  defineRoute({
    path: ["/project/:projectId/EstimateList"],
    element: EstimateListPage,
    params: { projectId: "objectId" },
  }),
  defineRoute({
    path: ["/project/:projectId/listTransmittals/:transmittalVersionId"],
    element: TransmitalDetails,
    params: { projectId: "objectId", transmittalVersionId: "objectId" },
  }),
  defineRoute({
    path: ["/project/:projectId/estimate/:estimateId", "/project/:projectId/estimate/:estimateId/version/:versionId"],
    element: Estimate,
    params: { projectId: "objectId", estimateId: "objectId", versionId: "objectId" },
    exact: true,
  }),
  defineRoute({ path: ["/transmittals"], element: TransmitalsList }),
  defineRoute({
    path: ["/project/:projectId/listTask"],
    element: TaskList,
    params: { projectId: "objectId" },
    exact: true,
  }),
  defineRoute({
    path: ["/project/:projectId/forms/:resourceId"],
    element: CreateInspectionAndProgres,
    params: { projectId: "objectId", resourceId: "objectId" },
    exact: true,
  }),
  defineRoute({
    path: ["/project/:projectId/listTask/:taskId"],
    element: TaskDetails,
    params: { projectId: "objectId", taskId: "objectId" },
  }),
  defineRoute({ path: ["/tasks"], element: TaskList }),
  defineRoute({
    path: [
      "/portalSettings/:settingType",
      "/portalSettings/:settingType/:formId/:formCategory",
      "/portalSettings/:settingType/:costDatabaseId/costDatabaseVersion/:costDatabaseVersionId",
      "/portalSettings/:settingType/checklists/:checklistId",
      "/portalSettings/:settingType/template/:nomenclatureTemplateId",
      "/portalSettings/:settingType/:costDatabaseId",
      "/portalSettings/:settingType/:projectId/projectUsers",
      "/portalSettings/:settingType/:userId/userProjects",
      "/portalSettings/:settingType/:organisationId/orgUsers",
    ],
    element: PortalSetting,
    params: {
      settingType: "slug",
      formId: "objectId",
      formCategory: "slug",
      costDatabaseId: "objectId",
      costDatabaseVersionId: "objectId",
      checklistId: "objectId",
      nomenclatureTemplateId: "objectId",
      projectId: "objectId",
      userId: "objectId",
      organisationId: "objectId",
    },
    exact: true,
    showStickySidebar: false,
    searchParams: { projectId: "objectId" },
  }),
  defineRoute({
    path: ["/admin-billing"],
    element: AdminBilling,
    showStickySidebar: false,
  }),
  defineRoute({
    path: ["/organisationSettings/:organisationId/:settingType?"],
    element: OrganisationSettings,
    params: { organisationId: "objectId", settingType: "slug" },
    showStickySidebar: false,
  }),
  defineRoute({
    path: ["/project/:projectId/projectdashboard"],
    element: ProjectDashboard,
    params: { projectId: "objectId" },
  }),
  defineRoute({
    path: ["/project/:projectId/Workspaces"],
    element: Workspaces,
    params: { projectId: "objectId" },
    searchParams: { projectId: "objectId" },
  }),
  defineRoute({
    path: ["/contractsList", "/project/:projectId/contractsList"],
    element: ContractsPage,
    params: { projectId: "objectId" },
    exact: true,
  }),
  defineRoute({
    path: ["/project/:projectId/contract/:contractId"],
    element: ContractDetails,
    params: { projectId: "objectId", contractId: "objectId" },
    exact: true,
  }),
  defineRoute({
    path: ["/project/:projectId/formsList/:moduleName"],
    element: FormsList,
    params: { projectId: "objectId", moduleName: "slug" },
    exact: true,
  }),
  defineRoute({
    path: ["/project/:projectId/scheduleList"],
    element: ScheduleListPage,
    params: { projectId: "objectId" },
    exact: true,
  }),
  defineRoute({
    path: [
      "/project/:projectId/schedule/:scheduleId/ScheduleDetails",
      "/project/:projectId/schedule/:scheduleId/version/:versionId",
    ],
    element: ScheduleDetailsNew,
    params: { projectId: "objectId", scheduleId: "objectId", versionId: "objectId" },
    exact: true,
  }),
  defineRoute({
    path: ["/designviewer/file/:fileId"],
    element: DesignViewer,
    params: { fileId: "objectId" },
    showHeader: false,
    showStickySidebar: false,
    searchParams: {
      transmittalId: "objectId",
      issueId: "objectId",
      clashTestId: "objectId",
      scheduleId: "objectId",
      scheduleVersionId: "objectId",
      measurementId: "uuid",
    },
  }),
  defineRoute({
    path: ["/project/:projectId/clashTests"],
    element: ClashTests,
    params: { projectId: "objectId" },
    exact: true,
  }),
  defineRoute({
    path: ["/project/:projectId/clashDetails/:clashTestId"],
    element: ClashTestDetails,
    params: { projectId: "objectId", clashTestId: "objectId" },
  }),
  defineRoute({
    path: ["/notifications"],
    element: NotificationsPage,
    showStickySidebar: false,
    searchParams: { ticketId: "objectId" },
  }),
  defineRoute({
    path: ["/escrowFiles/:batchId"],
    element: EscrowFiles,
    params: { batchId: "uuid" },
    showStickySidebar: false,
  }),
  defineRoute({
    path: ["/project/createNewProject"],
    element: CreateProject,
    exact: true,
  }),
  defineRoute({
    path: ["/estimatePortalList"],
    element: EstimatePortalList,
    exact: true,
    showStickySidebar: false,
  }),
  defineRoute({
    path: ["/review", "/reviews?byPortal=true"],
    element: ReviewPage,
    exact: true,
    searchParams: { projectId: "objectId" },
  }),
  defineRoute({
    path: [":module/pdfExport"],
    element: PdfGenerator,
    params: { module: "slug" },
    showStickySidebar: false,
    showHeader: false,
    searchParams: { projectId: "objectId" },
  }),
  defineRoute({
    path: ["/", "/newSignIn"],
    element: SignInUser,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/signupflow"],
    element: SignupFlow,
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/newInvitation"],
    element: InviteUser,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
    searchParams: { id: "objectId" },
  }),
  defineRoute({
    path: ["/reset-password"],
    element: ForgotPassword,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    // Declared BEFORE the bare token route: `matchRoute` builds a regex per path and takes the first
    // hit, and `/share/:token` would otherwise not match this deeper path anyway — but keeping the more
    // specific route first makes the intent explicit.
    //
    // The param MUST be named `:fileId`, matching `/designviewer/file/:fileId`: DesignViewer reads
    // `useParams().fileId` directly, so any other name here leaves it undefined. It was `:fileVersionId`
    // and that is exactly what broke — `setCurrentFileVersionId(undefined)` on a string-initialised
    // slice throws "A case reducer on a non-draftable value must not return undefined".
    path: ["/share/:token/file/:fileId"],
    element: SharedFileViewerPage,
    params: { token: "slug", fileId: "objectId" },
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/share/:token"],
    element: SharedLinkPage,
    params: { token: "slug" },
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  }),
  defineRoute({
    path: ["/ai/chat", "/ai/chat/:conversationId"],
    element: AiChat,
    params: { conversationId: "objectId" },
    showStickySidebar: false,
    showHeader: false,
  }),
];

export default routes;
